/**
 * 商朝势力与城邑数据
 * 基于甲骨文记载和考古发现
 */

// ==================== 类型定义 ====================

/** 势力类型 */
export type FactionType = 'royal' | 'vassal' | 'hostile' | 'independent' | 'nomadic'

/** 势力关系 */
export type FactionRelation = 'suzerain' | 'vassal' | 'ally' | 'neutral' | 'hostile' | 'at_war'

/** 邑的等级 */
export type CityLevel = 'capital' | 'large' | 'medium' | 'small'

/** 地形类型 */
export type TerrainType = 'plain' | 'mountain' | 'river' | 'desert' | 'grassland' | 'forest' | 'marsh' | 'coast'

/** 势力数据 */
export interface Faction {
  id: string
  name: string
  nameEn: string
  type: FactionType
  color: string
  description: string
  // 地理位置（大致经纬度，用于地图定位）
  location: {
    x: number // 经度相对位置 (0-100)
    y: number // 纬度相对位置 (0-100)
  }
  // 势力范围（多边形区域，用于地图绘制）
  territory: number[][] // [[x1,y1], [x2,y2], ...]
  // 初始关系
  initialRelation: FactionRelation
  // 特性
  traits: string[]
  // 初始难度
  difficulty: 1 | 2 | 3 | 4 | 5
  // 是否可玩
  playable: boolean
}

/** 城邑（邑）数据 */
export interface City {
  id: string
  name: string
  nameEn: string
  level: CityLevel
  // 所属势力
  factionId: string
  // 地理位置
  location: {
    x: number
    y: number
  }
  // 地形
  terrain: TerrainType
  // 人口规模
  population: number
  // 初始资源
  resources: {
    food: number
    money: number
    wood: number
    metal: number
  }
  // 特殊建筑
  buildings: string[]
  // 描述
  description: string
  // 是否为起始都城
  isStartingCapital: boolean
}

/** 剧本数据 */
export interface Scenario {
  id: string
  name: string
  era: 'early' | 'middle' | 'late'
  startYear: number
  description: string
  // 可选势力
  playableFactions: string[]
  // 初始都城
  startingCapitals: Record<string, string> // factionId -> cityId
  // 特殊事件
  specialEvents: string[]
}

// ==================== 商朝势力数据 ====================

export const SHANG_FACTIONS: Faction[] = [
  // 商王室 - 玩家默认势力
  {
    id: 'shang',
    name: '商',
    nameEn: 'Shang',
    type: 'royal',
    color: '#8B4513', // 青铜色
    description: '商王室，统治中原的共主。拥有发达的青铜文明和甲骨文，通过内外服制度管理广大疆域。',
    location: { x: 130, y: 55 },
    territory: [[100, 80], [120, 80], [130, 100], [120, 110], [100, 110], [90, 100]],
    initialRelation: 'suzerain',
    traits: ['青铜文明', '甲骨文', '内外服制度', '祖先崇拜'],
    difficulty: 2,
    playable: true,
  },
  // 敌对/边患方国
  {
    id: 'guifang',
    name: '鬼方',
    nameEn: 'Guifang',
    type: 'nomadic',
    color: '#4A4A4A', // 灰色
    description: '北方强大游牧民族，经常侵扰商朝边境。擅长骑射，机动性强。',
    location: { x: 95, y: 35 },
    territory: [[60, 40], [80, 40], [84, 60], [70, 70], [56, 60]],
    initialRelation: 'hostile',
    traits: ['游牧民族', '骑射精通', '劫掠成性', '来去如风'],
    difficulty: 3,
    playable: true,
  },
  {
    id: 'qiangfang',
    name: '羌方',
    nameEn: 'Qiangfang',
    type: 'nomadic',
    color: '#6B8E6B', // 草绿色
    description: '西方羌人部落，分布广泛。与商朝时战时和，是重要的奴隶来源。',
    location: { x: 45, y: 55 },
    territory: [[40, 70], [60, 70], [64, 90], [50, 100], [36, 90]],
    initialRelation: 'hostile',
    traits: ['羌人部落', '畜牧为主', '骁勇善战', '分布广泛'],
    difficulty: 3,
    playable: true,
  },
  {
    id: 'tufang',
    name: '土方',
    nameEn: 'Tufang',
    type: 'hostile',
    color: '#8B7355', // 土黄色
    description: '北方强敌，位于山西北部。经常与商朝发生战争。',
    location: { x: 105, y: 40 },
    territory: [[80, 50], [100, 50], [104, 70], [90, 76], [76, 70]],
    initialRelation: 'hostile',
    traits: ['北方强敌', '善战', '频繁侵扰'],
    difficulty: 3,
    playable: false,
  },
  {
    id: 'renfang',
    name: '人方',
    nameEn: 'Renfang',
    type: 'hostile',
    color: '#CD853F', // 秘鲁色
    description: '东夷部落，位于山东半岛和淮河流域。帝辛时期大规模征讨对象。',
    location: { x: 185, y: 75 },
    territory: [[140, 90], [170, 90], [176, 110], [160, 120], [136, 110]],
    initialRelation: 'hostile',
    traits: ['东夷部落', '青铜冶炼', '淮河流域', '频繁叛乱'],
    difficulty: 3,
    playable: true,
  },
  {
    id: 'yufang',
    name: '盂方',
    nameEn: 'Yufang',
    type: 'hostile',
    color: '#4682B4', // 钢蓝色
    description: '南方方国，位于河南南部、湖北北部。',
    location: { x: 125, y: 100 },
    territory: [[90, 120], [110, 120], [116, 140], [100, 144], [84, 140]],
    initialRelation: 'neutral',
    traits: ['南方方国', '长江流域', '水稻种植'],
    difficulty: 2,
    playable: false,
  },
  // 臣服方国
  {
    id: 'zhoufang',
    name: '周方',
    nameEn: 'Zhoufang',
    type: 'vassal',
    color: '#228B22', // 森林绿
    description: '西方诸侯，位于陕西关中。后期逐渐强大，最终灭商建立周朝。',
    location: { x: 55, y: 80 },
    territory: [[30, 80], [50, 80], [56, 100], [44, 110], [24, 100]],
    initialRelation: 'vassal',
    traits: ['西方诸侯', '农耕发达', '礼乐文化', '逐渐强大'],
    difficulty: 4,
    playable: true,
  },
  {
    id: 'zhaofang',
    name: '召方',
    nameEn: 'Zhaofang',
    type: 'vassal',
    color: '#9370DB', // 中紫色
    description: '西南诸侯，位于陕西南部。臣服于商，定期纳贡。',
    location: { x: 75, y: 95 },
    territory: [[50, 100], [70, 100], [74, 120], [60, 124], [46, 120]],
    initialRelation: 'vassal',
    traits: ['西南诸侯', '山地民族', '臣服纳贡'],
    difficulty: 2,
    playable: false,
  },
  {
    id: 'jingfang',
    name: '井方',
    nameEn: 'Jingfang',
    type: 'vassal',
    color: '#DAA520', // 金黄色
    description: '北方诸侯，位于河北南部。商朝重要盟友。',
    location: { x: 140, y: 38 },
    territory: [[110, 50], [130, 50], [134, 70], [120, 76], [106, 70]],
    initialRelation: 'vassal',
    traits: ['北方诸侯', '商朝盟友', '边境守卫'],
    difficulty: 2,
    playable: false,
  },
  {
    id: 'chongguo',
    name: '崇国',
    nameEn: 'Chong',
    type: 'vassal',
    color: '#B8860B', // 暗金色
    description: '西方诸侯，位于陕西西安附近。被周文王所灭。',
    location: { x: 60, y: 72 },
    territory: [[44, 90], [56, 90], [60, 104], [50, 108], [40, 104]],
    initialRelation: 'vassal',
    traits: ['西方屏障', '被周所灭', '历史名国'],
    difficulty: 2,
    playable: false,
  },
  {
    id: 'yuguo',
    name: '邘国',
    nameEn: 'Yu',
    type: 'vassal',
    color: '#BC8F8F', // 玫瑰褐色
    description: '西北诸侯，位于河南沁阳。臣服于商。',
    location: { x: 115, y: 52 },
    territory: [[90, 76], [102, 76], [104, 90], [96, 94], [88, 90]],
    initialRelation: 'vassal',
    traits: ['西北诸侯', '臣服纳贡'],
    difficulty: 1,
    playable: false,
  },
  // 独立文明
  {
    id: 'sanxingdui',
    name: '古蜀国',
    nameEn: 'Ancient Shu',
    type: 'independent',
    color: '#D4AF37', // 金色（代表黄金面具）
    description: '四川盆地的神秘文明，三星堆文化。拥有独特的青铜文化和黄金工艺，与中原商朝有文化交流但保持独立。',
    location: { x: 45, y: 120 },
    territory: [[10, 120], [30, 120], [36, 140], [24, 150], [6, 140]],
    initialRelation: 'neutral',
    traits: ['神秘文明', '黄金工艺', '独特青铜', '四川盆地', '独立发展'],
    difficulty: 4,
    playable: true,
  },
]

// ==================== 商朝城邑数据 ====================

export const SHANG_CITIES: City[] = [
  // 商王畿核心邑
  {
    id: 'yin',
    name: '殷',
    nameEn: 'Yin',
    level: 'capital',
    factionId: 'shang',
    location: { x: 130, y: 55 },
    terrain: 'plain',
    population: 15000,
    resources: { food: 5000, money: 3000, wood: 2000, metal: 1500 },
    buildings: ['王宫', '宗庙', '青铜工坊', '甲骨文库', '市场'],
    description: '盘庚迁殷后的都城，商朝晚期的政治、经济、文化中心。殷墟遗址所在地，拥有发达的青铜文明和甲骨文。',
    isStartingCapital: true,
  },
  {
    id: 'yanshi',
    name: '偃师',
    nameEn: 'Yanshi',
    level: 'large',
    factionId: 'shang',
    location: { x: 120, y: 68 },
    terrain: 'river',
    population: 8000,
    resources: { food: 4000, money: 2000, wood: 1500, metal: 1000 },
    buildings: ['西亳宫', '宗庙', '青铜工坊', '码头'],
    description: '西亳所在地，成汤灭夏后建立的早期都城。位于洛河之滨，控制东西交通要道。',
    isStartingCapital: false,
  },
  {
    id: 'zhengzhou',
    name: '郑州',
    nameEn: 'Zhengzhou',
    level: 'large',
    factionId: 'shang',
    location: { x: 128, y: 62 },
    terrain: 'plain',
    population: 7000,
    resources: { food: 3500, money: 1800, wood: 1200, metal: 800 },
    buildings: ['商城', '宗庙', '青铜工坊', '城墙'],
    description: '郑州商城，商朝早期重要都城。规模宏大，城墙周长达7公里。',
    isStartingCapital: false,
  },
  {
    id: 'chaoge',
    name: '朝歌',
    nameEn: 'Chaoge',
    level: 'medium',
    factionId: 'shang',
    location: { x: 138, y: 52 },
    terrain: 'plain',
    population: 5000,
    resources: { food: 2500, money: 1500, wood: 1000, metal: 600 },
    buildings: ['离宫', '宗庙', '市场'],
    description: '商朝别都，位于淇水之滨。帝辛时期的重要城市，后成为商朝最后的都城。',
    isStartingCapital: false,
  },
  {
    id: 'youli',
    name: '羑里',
    nameEn: 'Youli',
    level: 'small',
    factionId: 'shang',
    location: { x: 125, y: 50 },
    terrain: 'plain',
    population: 1500,
    resources: { food: 800, money: 400, wood: 300, metal: 200 },
    buildings: ['监狱', '驻军营地'],
    description: '著名监狱，周文王曾被囚禁于此，演绎《周易》。',
    isStartingCapital: false,
  },
  // 商王畿其他邑
  {
    id: 'xingyang',
    name: '荥阳',
    nameEn: 'Xingyang',
    level: 'medium',
    factionId: 'shang',
    location: { x: 122, y: 58 },
    terrain: 'river',
    population: 3500,
    resources: { food: 1800, money: 900, wood: 700, metal: 400 },
    buildings: ['粮仓', '码头'],
    description: '位于黄河之滨，重要的粮食产地和交通枢纽。',
    isStartingCapital: false,
  },
  {
    id: 'xinzheng',
    name: '新郑',
    nameEn: 'Xinzheng',
    level: 'medium',
    factionId: 'shang',
    location: { x: 132, y: 65 },
    terrain: 'plain',
    population: 3000,
    resources: { food: 1500, money: 800, wood: 600, metal: 350 },
    buildings: ['市场', '工坊'],
    description: '位于颍水流域，农业发达。',
    isStartingCapital: false,
  },
  {
    id: 'muye',
    name: '牧野',
    nameEn: 'Muye',
    level: 'medium',
    factionId: 'shang',
    location: { x: 128, y: 48 },
    terrain: 'plain',
    population: 2500,
    resources: { food: 1200, money: 600, wood: 500, metal: 300 },
    buildings: ['演武场', '军营'],
    description: '著名的牧野之战发生地，位于朝歌以南。',
    isStartingCapital: false,
  },
  // 周方城邑
  {
    id: 'qishan',
    name: '岐山',
    nameEn: 'Qishan',
    level: 'large',
    factionId: 'zhoufang',
    location: { x: 55, y: 80 },
    terrain: 'mountain',
    population: 4000,
    resources: { food: 2000, money: 1000, wood: 1500, metal: 600 },
    buildings: ['周原宫室', '宗庙', '农田'],
    description: '周人发祥地，位于岐山之下。周文王、武王在此积蓄力量。',
    isStartingCapital: true,
  },
  {
    id: 'fengjing',
    name: '丰京',
    nameEn: 'Fengjing',
    level: 'medium',
    factionId: 'zhoufang',
    location: { x: 52, y: 85 },
    terrain: 'river',
    population: 3500,
    resources: { food: 1800, money: 900, wood: 1200, metal: 500 },
    buildings: ['宫殿', '宗庙', '市场'],
    description: '周文王建立丰京，位于沣河西岸。',
    isStartingCapital: false,
  },
  {
    id: 'haojing',
    name: '镐京',
    nameEn: 'Haojing',
    level: 'medium',
    factionId: 'zhoufang',
    location: { x: 50, y: 88 },
    terrain: 'river',
    population: 3000,
    resources: { food: 1500, money: 800, wood: 1000, metal: 450 },
    buildings: ['宫殿', '军营'],
    description: '周武王建立镐京，位于沣河东岸，与丰京并称丰镐。',
    isStartingCapital: false,
  },
  // 鬼方城邑
  {
    id: 'guifangcheng',
    name: '鬼方城',
    nameEn: 'Guifang City',
    level: 'large',
    factionId: 'guifang',
    location: { x: 95, y: 35 },
    terrain: 'grassland',
    population: 3000,
    resources: { food: 1500, money: 500, wood: 800, metal: 400 },
    buildings: ['单于庭', '牧场', '骑兵营地'],
    description: '鬼方核心城邑，位于草原地带。鬼方是北方强大游牧民族。',
    isStartingCapital: true,
  },
  // 羌方城邑
  {
    id: 'qiangfangcheng',
    name: '羌方城',
    nameEn: 'Qiangfang City',
    level: 'medium',
    factionId: 'qiangfang',
    location: { x: 45, y: 55 },
    terrain: 'mountain',
    population: 2500,
    resources: { food: 1200, money: 400, wood: 1000, metal: 300 },
    buildings: ['部落议事厅', '牧场'],
    description: '羌方核心城邑，位于陇山一带。羌人分布广泛，部落众多。',
    isStartingCapital: true,
  },
  // 人方城邑
  {
    id: 'yishicheng',
    name: '夷师',
    nameEn: 'Yishi',
    level: 'large',
    factionId: 'renfang',
    location: { x: 78, y: 48 },
    terrain: 'coast',
    population: 3500,
    resources: { food: 1800, money: 900, wood: 1200, metal: 600 },
    buildings: ['东夷大殿', '渔港', '青铜工坊'],
    description: '人方核心城邑，位于山东半岛。东夷文化发达，擅长弓箭。',
    isStartingCapital: true,
  },
  // 古蜀国城邑
  {
    id: 'sanxingdui',
    name: '三星堆',
    nameEn: 'Sanxingdui',
    level: 'large',
    factionId: 'sanxingdui',
    location: { x: 45, y: 120 },
    terrain: 'plain',
    population: 4000,
    resources: { food: 2000, money: 1500, wood: 1800, metal: 2000 },
    buildings: ['青铜神殿', '黄金工坊', '祭祀坑', '宫殿'],
    description: '神秘的三星堆遗址，古蜀国的都城。拥有独特的青铜文化和黄金工艺，出土了大量青铜面具、黄金权杖等珍贵文物。',
    isStartingCapital: true,
  },
  {
    id: 'jinsha',
    name: '金沙',
    nameEn: 'Jinsha',
    level: 'medium',
    factionId: 'sanxingdui',
    location: { x: 42, y: 118 },
    terrain: 'river',
    population: 2500,
    resources: { food: 1200, money: 1000, wood: 1000, metal: 1200 },
    buildings: ['祭祀台', '黄金工坊'],
    description: '金沙遗址，古蜀国的重要城邑。出土了著名的太阳神鸟金箔。',
    isStartingCapital: false,
  },
  // 鬼方新增城邑
  {
    id: 'guifangbei',
    name: '鬼方北营',
    nameEn: 'Guifang North Camp',
    level: 'medium',
    factionId: 'guifang',
    location: { x: 90, y: 30 },
    terrain: 'grassland',
    population: 2000,
    resources: { food: 1000, money: 300, wood: 600, metal: 300 },
    buildings: ['牧场', '骑兵营地', '毡帐'],
    description: '鬼方北方重要营地，位于草原深处，是鬼方骑兵的主要训练地。',
    isStartingCapital: false,
  },
  {
    id: 'guifangnan',
    name: '鬼方南营',
    nameEn: 'Guifang South Camp',
    level: 'small',
    factionId: 'guifang',
    location: { x: 100, y: 40 },
    terrain: 'grassland',
    population: 1200,
    resources: { food: 600, money: 200, wood: 400, metal: 200 },
    buildings: ['牧场', '哨站'],
    description: '鬼方南方前哨，用于监视商朝北方边境。',
    isStartingCapital: false,
  },
  // 羌方新增城邑
  {
    id: 'qiangbei',
    name: '羌北',
    nameEn: 'Qiang North',
    level: 'small',
    factionId: 'qiangfang',
    location: { x: 40, y: 50 },
    terrain: 'mountain',
    population: 1000,
    resources: { food: 500, money: 200, wood: 800, metal: 200 },
    buildings: ['山寨', '牧场'],
    description: '羌人北方部落，位于陇山北麓，以畜牧业为主。',
    isStartingCapital: false,
  },
  {
    id: 'qiangnan',
    name: '羌南',
    nameEn: 'Qiang South',
    level: 'medium',
    factionId: 'qiangfang',
    location: { x: 48, y: 60 },
    terrain: 'mountain',
    population: 1800,
    resources: { food: 900, money: 300, wood: 900, metal: 250 },
    buildings: ['部落议事厅', '牧场', '山寨'],
    description: '羌人南方部落，与周方接壤，常有贸易往来。',
    isStartingCapital: false,
  },
  // 人方新增城邑
  {
    id: 'yixu',
    name: '夷墟',
    nameEn: 'Yixu',
    level: 'medium',
    factionId: 'renfang',
    location: { x: 75, y: 45 },
    terrain: 'coast',
    population: 2200,
    resources: { food: 1200, money: 600, wood: 800, metal: 400 },
    buildings: ['渔港', '市场', '弓箭工坊'],
    description: '人方东部城邑，以渔业和弓箭制造闻名。',
    isStartingCapital: false,
  },
  {
    id: 'yizhong',
    name: '夷中',
    nameEn: 'Yizhong',
    level: 'small',
    factionId: 'renfang',
    location: { x: 80, y: 50 },
    terrain: 'forest',
    population: 1500,
    resources: { food: 800, money: 400, wood: 1200, metal: 300 },
    buildings: ['伐木场', '弓箭工坊'],
    description: '人方中部城邑，位于森林地带，木材资源丰富。',
    isStartingCapital: false,
  },
]

// ==================== 商朝剧本数据 ====================

export const SHANG_SCENARIOS: Scenario[] = [
  {
    id: 'cheng-tang-revolution',
    name: '成汤革命',
    era: 'early',
    startYear: -1600,
    description: '成汤率领商族推翻夏朝，建立商朝。作为商王，你需要巩固统治，平定各方叛乱，建立内外服制度。',
    playableFactions: ['shang'],
    startingCapitals: { shang: 'yanshi' },
    specialEvents: ['鸣条之战', '建立商朝', '内外服制度'],
  },
  {
    id: 'pan-geng-move',
    name: '盘庚迁殷',
    era: 'middle',
    startYear: -1300,
    description: '盘庚力排众议，将都城迁至殷。面对贵族反对和方国叛乱，你需要稳定局势，开创商朝新的繁荣。',
    playableFactions: ['shang'],
    startingCapitals: { shang: 'yin' },
    specialEvents: ['迁都之议', '贵族反对', '殷墟建设'],
  },
  {
    id: 'wu-ding-prosperity',
    name: '武丁中兴',
    era: 'late',
    startYear: -1250,
    description: '商朝鼎盛时期，武丁王四处征伐，扩大疆域。你需要管理庞大的帝国，应对四方边患，同时发展青铜文明。',
    playableFactions: ['shang', 'guifang', 'qiangfang', 'renfang'],
    startingCapitals: {
      shang: 'yin',
      guifang: 'guifangcheng',
      qiangfang: 'qiangfangcheng',
      renfang: 'yishicheng',
    },
    specialEvents: ['妇好出征', '征伐鬼方', '征伐羌方', '征伐人方', '甲骨文鼎盛'],
  },
  {
    id: 'di-xin-disaster',
    name: '帝辛之祸',
    era: 'late',
    startYear: -1075,
    description: '帝辛（纣王）统治末年，暴政导致民怨沸腾，周文王在西部崛起。作为商王，你能逆转历史吗？',
    playableFactions: ['shang', 'zhoufang'],
    startingCapitals: {
      shang: 'chaoge',
      zhoufang: 'qishan',
    },
    specialEvents: ['文王被囚', '姜子牙出山', '比干之死', '鹿台自焚'],
  },
  {
    id: 'wu-wang-conquest',
    name: '武王伐纣',
    era: 'late',
    startYear: -1046,
    description: '周武王联合诸侯，发动牧野之战。你可以选择作为周武王推翻商朝，或作为帝辛保卫商朝。',
    playableFactions: ['zhoufang', 'shang'],
    startingCapitals: {
      zhoufang: 'haojing',
      shang: 'chaoge',
    },
    specialEvents: ['孟津之会', '牧野之战', '商朝灭亡', '周朝建立'],
  },
]

// ==================== 辅助函数 ====================

/** 根据ID获取势力 */
export function getFactionById(id: string): Faction | undefined {
  return SHANG_FACTIONS.find(f => f.id === id)
}

/** 根据ID获取城邑 */
export function getCityById(id: string): City | undefined {
  return SHANG_CITIES.find(c => c.id === id)
}

/** 获取势力的所有城邑 */
export function getCitiesByFaction(factionId: string): City[] {
  return SHANG_CITIES.filter(c => c.factionId === factionId)
}

/** 获取剧本 */
export function getScenarioById(id: string): Scenario | undefined {
  return SHANG_SCENARIOS.find(s => s.id === id)
}

/** 获取可玩势力 */
export function getPlayableFactions(): Faction[] {
  return SHANG_FACTIONS.filter(f => f.playable)
}

/** 获取势力的初始城邑数量 */
export function getFactionCityCount(factionId: string): number {
  return SHANG_CITIES.filter(c => c.factionId === factionId).length
}
